/**
 * Ce que les **trois écrans des transactions** partagent : les mises en forme,
 * la pastille d'état, et la façon de nommer une contrepartie.
 *
 * La recherche, la fiche d'une opération et la fiche d'un acteur montrent les
 * mêmes choses — un montant en unités mineures, un instant en UTC, un code
 * d'état, un acteur. Les écrire trois fois aurait fait trois mises en forme à
 * tenir alignées, et le jour de la divergence c'est le même montant affiché
 * différemment sur deux écrans, ce qui fait douter des deux.
 *
 * ## Les codes sont traduits, avec repli sur le code
 *
 * Le porteur l'a dit en regardant l'écran : « le texte des types de transaction
 * est mal formaté ». `P2P_TRANSFER` n'est pas un libellé, et le dessin écrit
 * « Transfert ».
 *
 * Le premier livré refusait de traduire, au motif qu'une table de
 * correspondance écrite dans le navigateur serait la liste blanche partagée que
 * `G3` refuse : il n'existe **nulle part** de liste centrale des types
 * d'opération — ils viennent de paramètres système —, et le premier type ajouté
 * par configuration rendrait la table fausse en silence.
 *
 * L'argument tenait contre une table **fermée**. Il ne tient pas contre une
 * table **ouverte** : la traduction est ici une couche d'affichage, et le repli
 * est le code lui-même. Un type inconnu s'affiche donc `RECEIVABLE_SETTLEMENT`
 * — lisible, exact, et visiblement non traduit — au lieu d'être masqué ou rendu
 * vide. C'est la différence entre une liste blanche et un dictionnaire.
 */

import { useCallback } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Party } from '../http/transactions.js';

/** L'adresse de la fiche d'un acteur. */
export function partyRoute(accountId: string): string {
  return `/transactions/accounts/${encodeURIComponent(accountId)}`;
}

/** L'adresse de la fiche d'une opération. */
export function transactionRoute(transactionId: string): string {
  return `/transactions/${encodeURIComponent(transactionId)}`;
}

/** Les mises en forme partagées par les trois écrans. */
export interface TransactionFormats {
  /** Un montant en unités mineures, dans la devise servie — ou sans devise. */
  readonly montant: (mineures: number, devise: string | null) => string;
  /** Un instant, entier et daté, en UTC. */
  readonly instant: (iso: string) => string;
  /** Une heure seule, en UTC — pour une colonne qui a une largeur. */
  readonly heure: (iso: string, memeJournee: boolean) => string;
  /** Une journée ISO, mise en forme pour l'œil. */
  readonly journee: (iso: string) => string;
  /** Le jour et le mois d'un instant, en UTC — la première ligne d'une cellule. */
  readonly journeeCourte: (iso: string) => string;
  /** Le libellé d'un type d'opération, ou son code. */
  readonly libelleType: (code: string) => string;
  /** Le libellé d'un état, ou son code. */
  readonly libelleEtat: (code: string) => string;
  /** Le libellé d'un canal, ou son code. */
  readonly libelleCanal: (code: string) => string;
  /** Le libellé de celui qui a lancé l'opération, ou son code. */
  readonly libelleInitiateur: (code: string) => string;
  /** Un taux servi, ou le fait qu'il n'y a rien à mesurer. */
  readonly taux: (valeur: number | null) => string;
}

/** Les mises en forme, dans la langue de l'agent. */
export function useTransactionFormats(): TransactionFormats {
  const { t, i18n } = useTranslation();

  /**
   * Un montant, dans la langue de l'agent — et **jamais une devise supposée**.
   *
   * `transactions.transaction` ne porte aucune devise : elle vit sur
   * `ledger.account`. Une opération sans compte rattaché n'en a donc pas, et le
   * montant est alors rendu en **unités mineures**, nommées comme telles. Y
   * écrire « F CFA » parce que c'est ce que la base contient aujourd'hui
   * afficherait un montant faux comme un fait.
   */
  const montant = useCallback(
    (mineures: number, devise: string | null): string => {
      if (devise === null) {
        return t('transactions.minorUnits', {
          amount: new Intl.NumberFormat(i18n.language).format(mineures),
        });
      }
      const format = new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: devise,
        currencyDisplay: 'narrowSymbol',
      });
      // Le nombre de décimales de la devise est demandé au navigateur plutôt
      // qu'écrit ici, où il figerait le franc CFA dans un écran qui n'a pas à
      // connaître les devises.
      const decimales = format.resolvedOptions().maximumFractionDigits ?? 0;
      return format.format(mineures / 10 ** decimales);
    },
    [i18n.language, t],
  );

  const instant = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'long',
        timeStyle: 'medium',
        timeZone: 'UTC',
      }).format(new Date(iso)),
    [i18n.language],
  );

  /**
   * L'heure d'une opération, telle qu'une colonne la montre.
   *
   * **En UTC**, comme la fenêtre que le serveur applique : lire dans le fuseau
   * du poste ferait apparaître des opérations « hors » de la période affichée,
   * et c'est l'écran où l'on arrête une journée comptable.
   */
  const heure = useCallback(
    (iso: string, memeJournee: boolean): string =>
      new Intl.DateTimeFormat(
        i18n.language,
        memeJournee
          ? { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
          : {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            },
      ).format(new Date(iso)),
    [i18n.language],
  );

  /**
   * Le jour et le mois d'un instant — la première ligne d'une cellule d'heure.
   *
   * **En UTC**, comme tout le reste de cet écran : la fenêtre est découpée en
   * UTC côté serveur, et une date lue dans le fuseau du poste ferait apparaître
   * des opérations « hors » de la période affichée.
   */
  const journeeCourte = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(iso)),
    [i18n.language],
  );

  const journee = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, { timeZone: 'UTC' }).format(
        new Date(`${iso}T00:00:00Z`),
      ),
    [i18n.language],
  );

  /**
   * Le libellé d'un code — **ou le code lui-même**.
   *
   * `t([clé, repli])` prend la première clé traduite. Le repli rend le code tel
   * quel : c'est ce qui distingue un dictionnaire d'une liste blanche, et c'est
   * ce qui permet à un type ajouté par configuration de s'afficher plutôt que
   * de disparaître.
   */
  const libelle = useCallback(
    (famille: string, code: string): string =>
      t([`transactions.${famille}.${code}`, 'transactions.rawCode'], { code }),
    [t],
  );

  const libelleType = useCallback((code: string) => libelle('types', code), [libelle]);
  const libelleEtat = useCallback((code: string) => libelle('statuses', code), [libelle]);
  const libelleCanal = useCallback((code: string) => libelle('channels', code), [libelle]);
  const libelleInitiateur = useCallback(
    (code: string) => libelle('initiators', code),
    [libelle],
  );

  /**
   * Le taux **servi** par le serveur, mis en forme — jamais calculé ici.
   *
   * `null` n'est pas zéro : c'est « aucune opération ». Un « 0 % » affiché sur
   * une période sans activité se lirait comme une panne générale du système de
   * paiement.
   */
  const taux = useCallback(
    (valeur: number | null): string =>
      valeur === null
        ? t('transactions.stats.noOperations')
        : new Intl.NumberFormat(i18n.language, {
            style: 'percent',
            maximumFractionDigits: 1,
          }).format(valeur / 100),
    [i18n.language, t],
  );

  return {
    montant,
    instant,
    heure,
    journee,
    journeeCourte,
    libelleType,
    libelleEtat,
    libelleCanal,
    libelleInitiateur,
    taux,
  };
}

/**
 * La **pastille d'état** du dessin — et sa couleur vient du serveur.
 *
 * Le dessin a trois fabriques de cellule : `OK()`, `WARN()`, `BAD()`. Le
 * premier livré ne les reprenait pas, au motif qu'elles portent un jugement sur
 * un code que le serveur rend sans le porter. Le porteur a regardé l'écran et a
 * tranché : « on n'arrive pas à différencier les statuts ». Sur un tableau
 * financier de vingt-cinq lignes, il a raison — un état lu, c'est un état qu'on
 * ne voit pas.
 *
 * **Le jugement n'est pas inventé pour autant** : `statusTone` est servi, et ce
 * sont les trois constantes que `TransactionStatus` déclare — réussie, en
 * cours, échouée. Le serveur les connaît déjà puisqu'il compte avec ; les
 * deviner ici aurait recréé la liste blanche de `G3`, et peint en vert un état
 * que le serveur n'appelle pas un succès. Un état que le domaine n'a pas encore
 * nommé arrive `OTHER`, et se peint **neutre** : le peindre en rouge
 * affirmerait un échec que personne n'a constaté.
 */
export function StatusPill({
  status,
  tone,
}: {
  readonly status: string;
  readonly tone: string;
}): ReactNode {
  const { libelleEtat } = useTransactionFormats();
  return (
    <span className="transactions__status" data-tone={tone}>
      {libelleEtat(status)}
    </span>
  );
}

/**
 * Une **contrepartie**, telle que l'écran la nomme — et le lien vers sa fiche.
 *
 * Trois cas, et ils se distinguent :
 *
 *  - un **client** : son nom, son numéro de téléphone et son numéro de compte.
 *    Un client dont la pièce n'a pas encore été déposée n'a **pas de nom
 *    enregistré** — c'est la population du pilote, et le cas qui a coûté
 *    `D-95` : l'écran le dit, il ne laisse pas de case vide ;
 *  - un **guichet** : son enseigne et son code. Un guichet n'est l'état civil
 *    de personne ;
 *  - un compte **interne** — cantonnement, revenus, commissions — : son numéro,
 *    qui est ce que ce compte *est*.
 *
 * Le lien mène à la fiche de l'acteur, et c'est ce que le porteur a demandé :
 * « des boutons pour aller sur une page détail de ces acteurs ». Il ne
 * déclenche aucune lecture tant qu'on ne clique pas — ouvrir une fiche est un
 * acte journalisé, et cinquante fiches ouvertes à l'affichage d'une liste
 * seraient cinquante consultations que personne n'a demandées.
 */
/**
 * Une contrepartie **cliquable** — sur la fiche d'une opération, et là
 * seulement.
 *
 * **La table n'en porte plus.** Le porteur a retiré la colonne des
 * contreparties — « enlève frais et Contreparties sur la table » — après qu'une
 * revue l'eut mesurée : deux cent cinquante-quatre points coupés en deux boîtes
 * de cent trois, tronquant sur vingt-cinq rangées sur vingt-cinq, où ne
 * tenaient que treize caractères de nom et douze de numéro — c'est-à-dire
 * l'indicatif national et rien d'autre. L'information n'est pas perdue : elle
 * est **ici**, en entier, sur la fiche qui a la place de la porter.
 *
 * Et c'était de toute façon le seul endroit où elle pouvait être un lien : une
 * rangée de la file s'ouvre elle-même au clic et à la touche Entrée, et un lien
 * posé dedans aurait été un second élément interactif dans une cible déjà
 * interactive — deux gestes concurrents au même endroit, et un parcours au
 * clavier qui traverse deux fois chaque ligne.
 */
export function PartyLink({ party }: { readonly party: Party | null }): ReactNode {
  const { t } = useTranslation();
  if (party === null) {
    return <span className="transactions__party-none">{t('transactions.cells.noParty')}</span>;
  }
  return (
    <Link className="transactions__party" to={partyRoute(party.accountId)}>
      <span className="transactions__party-name">{nomDeLaPartie(party, t)}</span>
      <span className="transactions__party-meta">{sousTitreDeLaPartie(party, t)}</span>
    </Link>
  );
}

/** Ce que la partie porte comme nom — ou ce qui en tient lieu. */
export function nomDeLaPartie(
  party: Party,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (party.displayName !== null) return party.displayName;
  if (party.outletName !== null) return party.outletName;
  // Ni client nommé, ni guichet : un compte interne, ou un client sans pièce
  // déposée. Les deux se disent, et ils ne se disent pas pareil.
  return party.customerId === null
    ? t('transactions.cells.internalAccount', { account: party.accountNumber })
    : t('transactions.cells.unnamedCustomer');
}

/** Ce que la partie porte sous son nom : le numéro, puis le compte. */
export function sousTitreDeLaPartie(
  party: Party,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (party.msisdn !== null) {
    return t('transactions.cells.customerMeta', {
      msisdn: party.msisdn,
      account: party.accountNumber,
    });
  }
  if (party.outletCode !== null) {
    return t('transactions.cells.outletMeta', {
      code: party.outletCode,
      account: party.accountNumber,
    });
  }
  return party.accountNumber;
}
